import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS } from '@bunker/shared';
import { Modal, TextField, Input, Skeleton } from '@heroui/react';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { useFriends } from '@/lib/useFriends';
import { api } from '@/lib/api';
import { fileToIconDataUrl, ICON_MAX_INPUT_BYTES, ImageUploadError } from '@/lib/imageUpload';
import { ImageCropModal } from '@/components/ImageCropModal';
import { avatarHue } from '@/lib/avatarHue';
import { CloseIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import { LoadError } from '@/components/ui/LoadError';
import type { DirectChannel, GuildMemberRow } from '@/types/api';

const EMPTY_DMS: Record<string, DirectChannel> = {};

/** A pickable group member — a guild member or a friend, normalised to one shape. */
interface Candidate {
  userId: string;
  nickname: string | null;
  user: { displayName: string | null; username: string; avatarUrl: string | null };
}

/** Group creator shared by the server and direct-message surfaces. */
export function GroupDmModal(props: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const dmChannels = useDataStore((s) => (props.open ? s.dmChannels : EMPTY_DMS));
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<GuildMemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberError, setMemberError] = useState<unknown>(null);
  const [memberReloadKey, setMemberReloadKey] = useState(0);
  // Group icon — same flow as the server-icon picker (crop → data URL), not a
  // bespoke uploader. '' = none.
  const [iconUrl, setIconUrl] = useState('');
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [iconError, setIconError] = useState<string | null>(null);
  const [iconBusy, setIconBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { friends } = useFriends();
  // A group can also start with someone already in a direct conversation.
  // Server nicknames take precedence when the same person is in this guild.
  const candidates = useMemo<Candidate[]>(() => {
    const byId = new Map<string, Candidate>();
    for (const m of members) {
      if (m.userId === me?.id) continue;
      byId.set(m.userId, {
        userId: m.userId,
        nickname: m.nickname,
        user: { displayName: m.user.displayName, username: m.user.username, avatarUrl: m.user.avatarUrl },
      });
    }
    for (const f of friends) {
      if (f.user.id === me?.id || byId.has(f.user.id)) continue;
      byId.set(f.user.id, {
        userId: f.user.id,
        nickname: null,
        user: { displayName: f.user.displayName, username: f.user.username, avatarUrl: f.user.avatarUrl },
      });
    }
    for (const dm of Object.values(dmChannels)) {
      for (const participant of dm.directParticipants) {
        if (participant.userId === me?.id || byId.has(participant.userId)) continue;
        byId.set(participant.userId, {
          userId: participant.userId,
          nickname: null,
          user: {
            displayName: participant.user.displayName,
            username: participant.user.username,
            avatarUrl: participant.user.avatarUrl,
          },
        });
      }
    }
    return [...byId.values()];
  }, [members, friends, dmChannels, me?.id]);

  useEffect(() => {
    if (!props.open) return;
    setName('');
    setSelected([]);
    setSearch('');
    setBusy(false);
    setIconUrl('');
    setCropFile(null);
    setIconError(null);
    setCreateError(null);
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    setMemberError(null);
    if (!currentGuildId) {
      setMembers([]);
      setLoadingMembers(false);
      return;
    }
    let cancelled = false;
    setLoadingMembers(true);
    api
      .getGuild(currentGuildId)
      .then((g) => {
        if (cancelled) return;
        setMembers(g.members);
        setMemberError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setMembers([]);
          setMemberError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.open, currentGuildId, memberReloadKey]);

  // Selected members resolved back to full GuildMemberRow objects, in
  // the order the user picked them, so the chip row matches click
  // history.
  const selectedMembers = selected
    .map((id) => candidates.find((m) => m.userId === id))
    .filter((m): m is Candidate => !!m);

  // Filter candidates by display name / handle. Selected members stay
  // visible even when filtered (so they can always be unchecked).
  const q = search.trim().toLowerCase();
  const filteredCandidates = q
    ? candidates.filter((m) => {
        const label = (m.nickname || m.user.displayName || m.user.username).toLowerCase();
        const handle = m.user.username.toLowerCase();
        return label.includes(q) || handle.includes(q) || selected.includes(m.userId);
      })
    : candidates;

  function toggle(userId: string) {
    setSelected((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  }

  // Same picker flow as the server-icon: GIF auto-crops, static images open the
  // cropper; the result is a square data URL the API stores directly.
  async function pickIcon(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconError(null);
    if (!file.type.startsWith('image/')) {
      setIconError(t('srv.iconOnlyImages'));
      return;
    }
    if (file.size > ICON_MAX_INPUT_BYTES) {
      setIconError(t('srv.iconTooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }));
      return;
    }
    if (file.type === 'image/gif') {
      setIconBusy(true);
      try {
        setIconUrl(await fileToIconDataUrl(file));
      } catch (err) {
        setIconError(err instanceof ImageUploadError ? err.message : t('srv.iconProcessFailed'));
      } finally {
        setIconBusy(false);
      }
      return;
    }
    setCropFile(file);
  }

  async function createGroup() {
    if (busy || selected.length < 2) return;
    setBusy(true);
    setCreateError(null);
    try {
      let channel = await api.createGroupDirectMessage({
        userIds: selected,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      // Optional avatar — the cropped data URL is stored straight on the group.
      if (iconUrl) {
        try {
          channel = await api.setGroupDirectMessageIcon(channel.id, iconUrl);
        } catch {
          /* group is created; icon is best-effort */
        }
      }
      upsertDmChannel(channel);
      selectDirectChannel(channel.id);
      props.onClose();
    } catch {
      // The actual create failed — surface it instead of leaving the user with
      // a stopped spinner and no group (the call site does `void createGroup()`,
      // so an unhandled rejection would otherwise vanish).
      setCreateError(t('gmod.groupCreateFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal.Backdrop
      className="app-viewport-overlay"
      isOpen={props.open}
      variant="blur"
      isDismissable={!busy}
      isKeyboardDismissDisabled={busy}
      onOpenChange={(o) => {
        if (!o) props.onClose();
      }}
    >
      <Modal.Container className="app-panel-container" placement="center">
        <Modal.Dialog
          aria-label={t('gmod.newGroupChat')}
          className="app-panel-dialog flex w-full max-w-lg flex-col overflow-hidden p-0"
        >
          <ImageCropModal
            file={cropFile}
            onCancel={() => setCropFile(null)}
            onCropped={(dataUrl) => { setIconUrl(dataUrl); setCropFile(null); }}
          />
          <div className="shrink-0 px-5 pb-2 pt-5 sm:px-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-ink">
                {t('gmod.newGroupChat')}
              </h2>
              <Tooltip label={t('gmod.close')}>
                <button
                  type="button"
                  onClick={props.onClose}
                  disabled={busy}
                  aria-label={t('gmod.close')}
                  className="grid size-11 shrink-0 place-items-center rounded-xl text-ink-muted transition-colors hover:bg-default/40 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="app-panel-body scrollbar min-h-0 space-y-5 px-5 py-3 sm:px-6">
            <div>
              <div className="flex items-end gap-3">
                {/* Group icon — reuses the server-icon picker style + crop flow. */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => void pickIcon(e)}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={iconBusy || busy}
                  aria-label={iconUrl ? t('srv.iconChange') : t('srv.iconUpload')}
                  className="group/av relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-default text-ink-muted transition-colors hover:bg-default/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {iconUrl ? (
                    <img src={iconUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                      <circle cx="12" cy="13" r="3.2" />
                    </svg>
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <span className="mb-1.5 block text-sm font-medium text-ink-muted">
                    {t('gmod.nameOptional')}
                  </span>
                  <TextField fullWidth aria-label={t('gmod.nameLabel')} value={name} onChange={(v) => setName(v.slice(0, 80))}>
                    <Input variant="secondary" maxLength={LIMITS.dm.groupNameMax} placeholder={t('gmod.groupNamePlaceholderShort')} />
                  </TextField>
                </div>
              </div>
              {iconError && <span className="mt-1.5 block text-xs text-danger">{iconError}</span>}
            </div>

            {selectedMembers.length > 0 && (
              <div>
                <div className="mb-1.5 flex items-baseline justify-between">
                  <span className="text-sm font-medium text-ink-muted">{t('gmod.selected')}</span>
                  <span className="text-xs text-ink-muted">
                    {t('gmod.memberCount', { count: selectedMembers.length })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedMembers.map((m) => {
                    const label = m.nickname || m.user.displayName || m.user.username;
                    const hue = avatarHue(m.userId);
                    return (
                      <button
                        key={m.userId}
                        type="button"
                        onClick={() => toggle(m.userId)}
                        className="group/chip inline-flex items-center gap-1.5 rounded-full bg-default/50 py-1 pl-1 pr-2 text-xs text-ink transition-colors hover:bg-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      >
                        <span
                          className={
                            'grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-full text-[9px] ' +
                            (m.user.avatarUrl ? 'bg-white/6' : `${hue.bg} ${hue.text}`)
                          }
                        >
                          {m.user.avatarUrl ? (
                            <img
                              src={m.user.avatarUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            label.charAt(0).toUpperCase()
                          )}
                        </span>
                        <span className="max-w-[100px] truncate">{label}</span>
                        <CloseIcon className="h-3 w-3 text-ink-muted transition-colors group-hover/chip:text-ink" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink-muted">
                {t('gmod.findMember')}
              </span>
              <TextField fullWidth aria-label={t('gmod.findMember')} value={search} onChange={setSearch}>
                <Input variant="secondary" placeholder={t('gmod.findMemberPlaceholder')} />
              </TextField>
            </div>

            <div className="-mx-1 px-1">
              {memberError !== null && filteredCandidates.length > 0 && (
                <LoadError error={memberError} fallback={t('gmod.loadCandidatesFailed')} onRetry={() => setMemberReloadKey((key) => key + 1)} className="py-3" />
              )}
              {loadingMembers && filteredCandidates.length === 0 ? (
                <ul className="flex flex-col gap-1" aria-busy>
                  {[0, 1, 2, 3].map((i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 rounded-lg px-2.5 py-2"
                    >
                      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                      <span className="flex flex-1 flex-col gap-1.5">
                        <Skeleton className="h-3 w-1/2 rounded" />
                        <Skeleton className="h-2.5 w-1/3 rounded" />
                      </span>
                    </li>
                  ))}
                </ul>
              ) : memberError !== null && filteredCandidates.length === 0 ? (
                <LoadError error={memberError} fallback={t('gmod.loadCandidatesFailed')} onRetry={() => setMemberReloadKey((key) => key + 1)} className="py-6" />
              ) : filteredCandidates.length === 0 ? (
                <div className="grid place-items-center py-6 text-center text-sm text-ink-muted">
                  {q ? t('gmod.noMatch', { query: q }) : t('gmod.listEmpty')}
                </div>
              ) : (
                <ul className="flex flex-col gap-1">
                  {filteredCandidates.map((m) => {
                    const label = m.nickname || m.user.displayName || m.user.username;
                    const hue = avatarHue(m.userId);
                    const isSelected = selected.includes(m.userId);
                    return (
                      <li key={m.userId}>
                        <button
                          type="button"
                          onClick={() => toggle(m.userId)}
                          aria-pressed={isSelected}
                          className={
                            'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ' +
                            (isSelected
                              ? 'bg-accent/12'
                              : 'hover:bg-default/40')
                          }
                        >
                          <span
                            className={
                                'grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full text-xs ' +
                              (m.user.avatarUrl
                                ? 'bg-white/6'
                                : `rounded-full ${hue.bg} ${hue.text}`)
                            }
                          >
                            {m.user.avatarUrl ? (
                              <img
                                src={m.user.avatarUrl}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              label.charAt(0).toUpperCase()
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-ink">
                              {label}
                            </span>
                            <span className="block truncate text-[11px] text-ink-muted">
                              @{m.user.username}
                            </span>
                          </span>
                          <span
                            aria-hidden
                            className={
                              'grid h-5 w-5 shrink-0 place-items-center rounded-full transition-[background-color,border-color,color,box-shadow,opacity] duration-150 ' +
                              (isSelected ? 'bg-accent text-bg' : 'bg-transparent')
                            }
                            style={
                              isSelected
                                ? undefined
                                : {
                                    boxShadow:
                                      'inset 0 0 0 1.5px rgb(var(--ring-inset) / 0.4)',
                                  }
                            }
                          >
                            {isSelected && (
                              <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-5 pt-3 sm:px-6">
            <span
              role={createError ? 'alert' : undefined}
              className={
                'text-xs ' +
                (createError
                  ? 'text-danger'
                  : selected.length >= 2
                    ? 'text-ink-muted'
                    : 'text-ink-muted')
              }
            >
              {createError
                ? createError
                : selected.length < 2
                  ? t('gmod.pickMin2')
                  : t('gmod.readyInChat', { count: selected.length })}
            </span>
            <button
              type="button"
              disabled={busy || selected.length < 2}
              onClick={() => void createGroup()}
              className="min-h-11 shrink-0 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-bg transition-colors hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? t('gmod.creating') : t('gmod.createChat')}
            </button>
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
